import { Link } from '@tanstack/react-router';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils/cn';
import type { NavGroup } from './types';

// On the dark-teal rail: active = solid light-turquoise pill; selected = a
// turquoise-tinted wash + ring; inactive = light icon with turquoise hover.
const iconBgActive =
  'bg-brand-accent/30 text-sidebar-foreground ring-1 ring-inset ring-brand-accent/40';
const iconBgSelected =
  'bg-brand-accent/20 text-sidebar-foreground ring-1 ring-brand-accent/40';
const iconBgInactive =
  'text-sidebar-foreground/90 group-hover:bg-brand-accent/25 group-hover:text-sidebar-foreground';

export function StripGroup({
  group,
  selected,
  anyChildActive,
  firstChildHref,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  group: NavGroup;
  selected: boolean;
  anyChildActive: boolean;
  firstChildHref?: string;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const cls = 'group flex justify-center w-full cursor-pointer';
  const inner = (
    <span
      className={cn(
        'flex items-center justify-center w-8 h-8 rounded-(--sidebar-icon-radius) transition-colors',
        anyChildActive
          ? iconBgActive
          : selected
            ? iconBgSelected
            : iconBgInactive,
      )}
    >
      <i
        className={cn(
          group.icon.replace(/\bfa-(thin|light)\b/, 'fa-regular'),
          'text-base',
        )}
      />
    </span>
  );

  if (firstChildHref && !selected) {
    return (
      <Tooltip delay={150}>
        <TooltipTrigger
          render={
            <Link
              to={firstChildHref as never}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className={cls}
            />
          }
        >
          {inner}
        </TooltipTrigger>
        <TooltipContent side='bottom' sideOffset={8}>
          {group.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip delay={150}>
      <TooltipTrigger
        render={
          <button
            type='button'
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={cls}
          />
        }
      >
        {inner}
      </TooltipTrigger>
      <TooltipContent side='bottom' sideOffset={8}>
        {group.label}
      </TooltipContent>
    </Tooltip>
  );
}
